import { useState } from 'react';
import { ClearOutlined, DeleteOutlined } from '@ant-design/icons';
import { useBoolean } from 'ahooks';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
} from 'antd';
import dayjs from 'dayjs';

import { MilesText } from 'components/Typography';

import { IPeriod, TFieldProps } from 'types';

import {
  getDisabledDiapasons, getFilteredDiapasons,
  handleChangeDates, handleFilterDates, handleRemoveDate,
} from './lib';

const { Item } = Form;
const { RangePicker } = DatePicker;

interface IDateDiapasonsProps extends Pick<TFieldProps, 'disabled' | 'allowEmpty'> {
  value?: IPeriod[];
  onChange?: (value: IPeriod[]) => void;
}

const DateDiapasons = ({
  value = [],
  disabled,
  onChange,
}: IDateDiapasonsProps) => {
  const [isFilterMode, setIsFilterMode] = useState(true);
  const [isDisabled, { setTrue, setFalse }] = useBoolean(true);
  const [rangePickerValue, setRangePickerValue] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>(() => [
    dayjs().startOf('day'),
    dayjs().endOf('day').add(1, 'month'),
  ]);

  const handleScheduleMore = () => {
    setRangePickerValue([null, null]);
    setIsFilterMode(false);
    setFalse();
  };

  const handleFilterMore = () => {
    setIsFilterMode(true);
    setFalse();
  };

  const handleClearFilter = () => {
    setRangePickerValue([null, null]);
    setTrue();
  };

  return (
    <>
      <RangePicker
        allowEmpty
        inputReadOnly
        value={isFilterMode ? rangePickerValue : [null, null]}
        format='YYYY/MM/DD'
        disabled={isDisabled}
        showTime={false}
        className='w-full'
        allowClear={false}
        onChange={isFilterMode
          ? handleFilterDates(setTrue, setRangePickerValue)
          : handleChangeDates(setTrue, value, onChange)}
        disabledDate={isFilterMode ? undefined : getDisabledDiapasons(value)}
      />

      {value?.filter(getFilteredDiapasons(rangePickerValue)).map(({ effectiveFrom, effectiveTo }, index) => (
        <Row
          key={effectiveFrom?.format('YYYY/MM/DD')}
          className='mt-2 pb-2 border-solid border-0 border-b border-[#d9d9d9]'
        >
          <Col xs={24}>
            <Row gutter={4}>
              <Col flex={1}>
                <MilesText className='pl-2'>
                  {effectiveFrom?.format('YYYY/MM/DD')}
                </MilesText>
              </Col>

              <Col flex={1}>
                <MilesText className='pl-2'>
                  {effectiveTo?.format('YYYY/MM/DD')}
                </MilesText>
              </Col>
            </Row>
          </Col>

          <div className='absolute right-[-40px]'>
            <Button
              danger
              size='small'
              onClick={handleRemoveDate(index, value, onChange)}
            >
              <DeleteOutlined />
            </Button>
          </div>
        </Row>
      ))}

      <Button
        size='small'
        className='mt-2'
        onClick={handleScheduleMore}
        disabled={disabled || !isDisabled}
      >
        Schedule More
      </Button>

      <Button
        size='small'
        className='mt-2 ml-2'
        onClick={handleFilterMore}
        disabled={disabled || !isDisabled}
      >
        Filter More
      </Button>

      <Button
        danger
        size='small'
        className='mt-2 ml-2'
        onClick={handleClearFilter}
        disabled={disabled || !isDisabled}
        icon={<ClearOutlined />}
      >
        Clear Filter
      </Button>
    </>
  );
};

export const FormDateDiapasons = ({
  name,
  label,
  rules,
  tooltip,
  disabled,
  itemClassName,
}: Omit<TFieldProps, 'type'>) => (
  <Item
    name={name}
    rules={rules}
    label={label}
    tooltip={tooltip}
    labelAlign='left'
    className={itemClassName}
  >
    <DateDiapasons disabled={disabled} />
  </Item>
);
